import { ListItem, ListItemText, Stack, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import shadows from "@mui/material/styles/shadows";
import React from "react";
import TextButton from "@/components/common/buttons/textButton";
import { useDrawerContext } from "@/components/common/drawer/_drawerContext";
import { ChevronSmallDownIcon, UserIcon } from "@/components/common/icons";
import InfiniteScrollAutocomplete from "@/components/common/infiniteScroll/infiniteScrollAutocomplete";
import { GREY, VIOLET } from "@/config/mui/colorPalette";
import { ClientForAppointmentCreationQuery } from "@/graphql/queries/clients.graphql.types";
import { useClients } from "@/hooks/clients/useClients";
import useClientsSorting from "@/hooks/clients/useClientsSorting";
import usePreselectedClients from "@/hooks/common/usePreselectedClients";
import useSearchQuery from "@/hooks/misc/useSearchQuery";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";

export type ClientForAppointmentCreation = NonNullable<
  ClientForAppointmentCreationQuery["clientByPk"]
>;

export const ClientSection = ({
  onSelect,
  onCreate,
  client,
  disabled,
}: {
  onSelect: (client: ClientForAppointmentCreation) => void;
  onCreate: () => void;
  client: ClientForAppointmentCreation | null;
  disabled?: boolean;
}) => {
  const { searchQuery, handleSearchInput, searchInputRef, resetSearch } =
    useSearchQuery();
  const { isOpen } = useDrawerContext();

  const { activeSortBy } = useClientsSorting("names-a-z");
  const {
    clients,
    loading,
    pagination: { hasNextPage, fetchNextPage },
  } = useClients({
    searchQuery,
    sortBy: activeSortBy,
    withPagination: true,
    skip: !isOpen,
  });

  const preselectedClients = usePreselectedClients(clients, client);

  return (
    <Stack gap={1.5} alignItems="flex-start">
      <Typography variant="labelTiny" color="text.secondary">
        Client
      </Typography>
      <InfiniteScrollAutocomplete<ClientForAppointmentCreation>
        disablePortal
        disabled={disabled}
        popupIcon={
          <ChevronSmallDownIcon
            color={disabled ? GREY[30] : VIOLET[90]}
            size={24}
          />
        }
        filterOptions={(options) => options}
        loading={loading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            placeholder="Choose client"
            variant="outlined"
            value={searchQuery}
            onChange={(event) => handleSearchInput?.(event)}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: <UserIcon color={GREY[50]} />,
                size: "small",
                inputRef: searchInputRef,
                sx: {
                  backgroundColor: (theme) =>
                    params.disabled ? GREY[15] : theme.palette.background.paper,
                  pl: "12px !important",
                  py: "8px !important",
                },
              },
            }}
          />
        )}
        onClose={resetSearch}
        onChange={(_, value) => onSelect(value)}
        value={client}
        slotProps={{
          paper: {
            sx: { boxShadow: shadows[4], borderRadius: 2, mt: 1 },
          },
        }}
        sx={{ width: "100%" }}
        options={preselectedClients || []}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        renderOption={(
          props: React.HTMLAttributes<HTMLLIElement> & { key: any },
          option: ClientForAppointmentCreation
        ) => (
          <ListItem
            component="li"
            {...props}
            key={option.id}
            sx={{ px: "12px !important", py: "8px !important" }}
          >
            <ListItemText
              sx={{ m: 0 }}
              primary={`${option.firstName} ${option.lastName}`}
              secondary={formatPhoneNumber(option.phone, "dashed")}
              slotProps={{
                primary: { variant: "labelSmall" },
                secondary: {
                  variant: "labelTiny",
                  color: "text.secondary",
                },
              }}
            />
          </ListItem>
        )}
      />
      {!disabled && (
        <TextButton
          onClick={onCreate}
          textVariant="labelSmall"
          sx={{ textAlign: "left" }}
        >
          Create new client
        </TextButton>
      )}
    </Stack>
  );
};
